import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ablyConnectionManager } from '../../utils/miscellaneous/ablyConnectionManager';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { INACTIVITY_LOGOUT_ACTIVITY_EVENT } from '../../hooks/useInactivityLogout';
import PortalDeliveryCallWorkspace from './PortalDeliveryCallWorkspace';
import {
  acceptPortalDeliveryVideoCall,
  disablePortalDeliveryVideoCallAutoPickupSetting,
  declinePortalDeliveryVideoCall,
  enablePortalDeliveryVideoCallAutoPickupSetting,
  endPortalDeliveryVideoCall,
  fetchPortalDeliveryVideoCallAutoPickupSetting,
  fetchPortalDeliveryVideoCall,
  fetchPortalDeliveryVideoCallPending,
} from './deliveryVideoCallApi';
import { getAppDateString } from '../../utils/miscellaneous/handoverEvents';
import medicationRingtone from '../../assets/audo&video/medication.mp3';
import { preloadDeliveryCallStream } from './deliveryCallStreamSource';
import { useAdminStealthHidden } from '../../hooks/useAdminStealth';
import {
  claimDeliveryVideoCallTabLock,
  releaseDeliveryVideoCallTabLock,
} from './deliveryVideoCallTabLock';

const DUPLICATE_REQUEST_WINDOW_MS = 4000;
// Slow enough to read, fast enough to catch the eye in a taskbar. No patient
// name here: tab titles show up in taskbar previews and screen shares.
const INCOMING_CALL_TITLE = '📞 Incoming call';
const TITLE_FLASH_INTERVAL_MS = 1000;
const AUTO_PICKUP_STATUS_REFRESH_MS = 60000;
const DELIVERY_VIDEO_CALL_TAB_EVENT = 'delivery-video-call-tab-event';
const TERMINAL_CALL_STATUSES = ['declined', 'cancelled', 'expired', 'ended'];
const activeIncomingRingSounds = new Set();

const stopAllIncomingRingSounds = () => {
  activeIncomingRingSounds.forEach((audio) => {
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = true;
      audio.volume = 0;
      audio.removeAttribute('src');
      audio.load();
    } catch (error) {}
  });
  activeIncomingRingSounds.clear();
};

const getCallIdKey = (callId) => String(callId ?? '');

const normalizeCall = (data) => {
  if (!data) return null;
  const normalized = {
    callId: data.call_id ?? data.callId,
    patientId: data.patient_id ?? data.patientId ?? data.patient?.id,
    patientFirstName: data.patient?.first_name ?? data.patientFirstName,
    patientLastName: data.patient?.last_name ?? data.patientLastName,
    patientPreferredName: data.patient?.preferred_name ?? data.patient_preferred_name ?? data.patientPreferredName,
    patientName: data.patient_name ?? data.patientName ?? [data.patient?.last_name, data.patient?.first_name].filter(Boolean).join(' ').trim(),
    driverId: data.driver?.id != null ? String(data.driver.id) : (data.driverId != null ? String(data.driverId) : undefined),
    driverName: data.driver?.name ?? data.driverName,
    pharmacistId: data.pharmacist?.id ?? data.pharmacistId,
    pharmacistName: data.pharmacist?.name ?? data.pharmacistName,
    status: data.status,
    patient_verification: data.patient_verification ?? data.patientVerification ?? null,
    verified_stamp: data.verified_stamp ?? data.verifiedStamp ?? data.patient_verification?.verified_stamp ?? data.patientVerification?.verified_stamp ?? null,
  };

  // The Vonage credentials and the video-stream fields exist only on the accept
  // response. Emitting them unconditionally would let a later merge from a
  // token-less GET/ring payload overwrite a good token with undefined, tripping
  // the workspace's "missing session details" guard. Only include when present.
  if (data.vonage_session_id != null) normalized.vonage_session_id = data.vonage_session_id;
  if (data.vonage_token != null) normalized.vonage_token = data.vonage_token;
  if (data.application_id != null) normalized.application_id = data.application_id;
  // Which SDK to use, and where to connect. The session/token slots above are
  // reused for MediTalk (LiveKit room ref + JWT), so only these two are new.
  // application_id is empty for MediTalk on purpose.
  if (data.video_provider != null) normalized.video_provider = data.video_provider;
  if (data.livekit_url != null) normalized.livekit_url = data.livekit_url;
  if (data.video_stream_enabled !== undefined) normalized.video_stream_enabled = data.video_stream_enabled;
  if (data.video_stream_url !== undefined) normalized.video_stream_url = data.video_stream_url;

  return normalized;
};

const pruneOldEntries = (mapRef, now, ttl = DUPLICATE_REQUEST_WINDOW_MS) => {
  mapRef.current.forEach((timestamp, key) => {
    if (now - timestamp > ttl) {
      mapRef.current.delete(key);
    }
  });
};

const buildDefaultAutoPickupSetting = () => ({
  allowed: false,
  enabled: false,
  activeDate: getAppDateString(),
  enabledAt: null,
  disabledAt: null,
});

// `allowed` is the platform admin's per-pharmacy switch: unless it is on, the
// pharmacist is never offered auto pickup and it can never be active.
const normalizeAutoPickupSetting = (data) => ({
  allowed: Boolean(data?.allowed),
  enabled: Boolean(data?.allowed) && Boolean(data?.enabled),
  activeDate: data?.active_date ?? getAppDateString(),
  enabledAt: data?.enabled_at ?? null,
  disabledAt: data?.disabled_at ?? null,
});

const notifyInactivityLogoutActivity = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(INACTIVITY_LOGOUT_ACTIVITY_EVENT));
  }
};

const createIncomingRingSound = () => {
  if (typeof window === 'undefined') return null;

  const audio = new Audio(medicationRingtone);
  audio.loop = true;
  audio.preload = 'auto';
  audio.volume = 0.9;
  activeIncomingRingSounds.add(audio);
  let stopped = false;
  let playbackBlocked = false;
  let blockedHandler = null;

  const play = async () => {
    if (stopped) return;

    try {
      await audio.play();
      if (stopped) {
        audio.pause();
        audio.currentTime = 0;
        return;
      }
      playbackBlocked = false;
    } catch (error) {
      playbackBlocked = true;
      blockedHandler?.();
    }
  };

  play();

  return {
    onBlocked: (handler) => {
      blockedHandler = handler;
      if (playbackBlocked) {
        blockedHandler?.();
      }
    },
    resume: async () => {
      if (stopped) return false;

      try {
        await audio.play();
        playbackBlocked = false;
        return true;
      } catch (error) {
        playbackBlocked = true;
        blockedHandler?.();
      }

      return false;
    },
    stop: async () => {
      stopped = true;
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = true;
        audio.volume = 0;
        audio.removeAttribute('src');
        audio.load();
      } catch (error) {}
      activeIncomingRingSounds.delete(audio);
    },
  };
};

const PortalDeliveryVideoCallManager = () => {
  const { pharmacyId, loginUserID, authData } = useCustomSelectors();
  const [incomingCall, setIncomingCall] = useState(null);
  const [showIncomingPopup, setShowIncomingPopup] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callNotice, setCallNotice] = useState(null);
  const [waitingCalls, setWaitingCalls] = useState([]);
  const [loadingAccept, setLoadingAccept] = useState(false);
  const [processingCallId, setProcessingCallId] = useState(null);
  const [confirmingWaitingCallId, setConfirmingWaitingCallId] = useState(null);
  const [currentAppDate, setCurrentAppDate] = useState(() => getAppDateString());
  const [autoPickupSetting, setAutoPickupSetting] = useState(() => buildDefaultAutoPickupSetting());
  const [isAutoPickupUpdating, setIsAutoPickupUpdating] = useState(false);
  const [isRingtoneBlocked, setIsRingtoneBlocked] = useState(false);
  const [isAcceptingCallFlow, setIsAcceptingCallFlow] = useState(false);
  const [ringtoneSuppressedCallIds, setRingtoneSuppressedCallIds] = useState(() => new Set());
  const incomingCallRef = useRef(null);
  const activeCallRef = useRef(null);
  const waitingCallsRef = useRef([]);
  const autoPickupSettingRef = useRef(buildDefaultAutoPickupSetting());
  const acceptingIncomingCallIdsRef = useRef(new Set());
  const sessionSubscriptionIdRef = useRef(null);
  const callNoticeTimeoutRef = useRef(null);
  const ringSoundRef = useRef(null);
  const processedMessageIdsRef = useRef(new Map());
  const processedRequestIdsRef = useRef(new Map());
  const acceptingRingtoneSuppressedCallIdsRef = useRef(new Set());
  const isAcceptingCallFlowRef = useRef(false);
  const inFlightDetailsFetchRef = useRef(new Set());
  const isSwitchingCallsRef = useRef(false);
  const lastPendingRecoveryCheckAtRef = useRef(0);
  const tabIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const tabBroadcastChannelRef = useRef(null);

  const pharmacistId = useMemo(() => loginUserID ?? authData?.data?.pharmacyloginuser?.[0]?.id ?? null, [loginUserID, authData]);
  const pharmacistName = useMemo(() => authData?.data?.pharmacyloginuser?.[0]?.name ?? authData?.data?.name ?? 'Pharmacist', [authData]);
  // The clip URL also rides on the logged-in user's profile, so it can be warmed
  // up long before a call arrives. The accept payload is what actually decides
  // whether it gets published.
  // ADMIN SCREEN PRIVACY (F9 x5): while hidden the clip is not used, so do not
  // warm it into memory either — there is nothing to play it. Resolving to null
  // leaves the preload effect below doing exactly what it does for a pharmacist
  // who has no clip configured: nothing.
  const adminStealthHidden = useAdminStealthHidden();
  const ownVideoStreamUrl = useMemo(() => {
    if (adminStealthHidden) return null;
    const user = authData?.data?.pharmacyloginuser?.[0];
    return user?.video_stream_enabled ? (user?.video_stream_url ?? null) : null;
  }, [authData, adminStealthHidden]);
  const incomingCallId = incomingCall?.callId ?? null;
  const waitingNotificationsHost = typeof document !== 'undefined' ? document.body : null;
  const autoPickupEnabledForToday = autoPickupSetting.enabled && autoPickupSetting.activeDate === currentAppDate;
  const shouldPlayIncomingRingtone = Boolean(
    !activeCall?.callId
    && !isAcceptingCallFlow
    && (
      (showIncomingPopup && incomingCallId && !ringtoneSuppressedCallIds.has(getCallIdKey(incomingCallId)))
      || waitingCalls.some((call) => !ringtoneSuppressedCallIds.has(getCallIdKey(call.callId)))
    )
  );

  const getCallDisplayName = (call) => call?.patientName || '';
  const getCallReferenceName = (call) => call?.patientPreferredName || '';

  const applyAutoPickupSetting = (nextSetting) => {
    const normalized = normalizeAutoPickupSetting(nextSetting);
    autoPickupSettingRef.current = normalized;
    setAutoPickupSetting(normalized);
    return normalized;
  };

  const isAutoPickupEnabledForToday = () => {
    const setting = autoPickupSettingRef.current;
    return Boolean(setting?.enabled) && setting?.activeDate === getAppDateString();
  };

  // Warm the clip into memory while the pharmacist is idle so the first call
  // starts playing with no network wait.
  useEffect(() => {
    if (!ownVideoStreamUrl) return;
    preloadDeliveryCallStream(ownVideoStreamUrl);
  }, [ownVideoStreamUrl]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    waitingCallsRef.current = waitingCalls;
  }, [waitingCalls]);

  useEffect(() => {
    autoPickupSettingRef.current = autoPickupSetting;
  }, [autoPickupSetting]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleCrossTabMessage = (message) => {
      if (
        message?.type !== 'accepted'
        || message?.sourceTabId === tabIdRef.current
        || (message?.pharmacyId != null && pharmacyId != null && String(message.pharmacyId) !== String(pharmacyId))
      ) {
        return;
      }

      handleCrossTabCallAccepted(message.callIds);
    };

    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(DELIVERY_VIDEO_CALL_TAB_EVENT);
      tabBroadcastChannelRef.current = channel;
      channel.onmessage = (event) => {
        handleCrossTabMessage(event?.data);
      };
    }

    const handleStorage = (event) => {
      if (event.key !== DELIVERY_VIDEO_CALL_TAB_EVENT || !event.newValue) return;

      try {
        handleCrossTabMessage(JSON.parse(event.newValue));
      } catch (error) {}
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
      tabBroadcastChannelRef.current?.close?.();
      tabBroadcastChannelRef.current = null;
    };
  }, [pharmacyId]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const nextDate = getAppDateString();
      setCurrentAppDate((previousDate) => previousDate === nextDate ? previousDate : nextDate);
    }, AUTO_PICKUP_STATUS_REFRESH_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!pharmacyId || !pharmacistId) {
      applyAutoPickupSetting(buildDefaultAutoPickupSetting());
      return undefined;
    }

    const loadAutoPickupSetting = async () => {
      try {
        const response = await fetchPortalDeliveryVideoCallAutoPickupSetting();
        if (cancelled) return;
        applyAutoPickupSetting(response);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching delivery auto pickup setting:', error);
        }
      }
    };

    loadAutoPickupSetting();

    return () => {
      cancelled = true;
    };
  }, [currentAppDate, pharmacyId, pharmacistId]);

  const resetCallTracking = (callId) => {
    if (!callId) return;
    inFlightDetailsFetchRef.current.delete(callId);
    processedRequestIdsRef.current.delete(`${callId}:requesting`);
  };

  const clearPrimaryIncoming = () => {
    incomingCallRef.current = null;
    setIncomingCall(null);
    setShowIncomingPopup(false);
  };

  const showPrimaryIncoming = (callDetails) => {
    if (!callDetails?.callId) return;
    incomingCallRef.current = callDetails;
    setIncomingCall(callDetails);
    setShowIncomingPopup(true);
  };

  const updateQueuedCall = (callDetails) => {
    if (!callDetails?.callId) return;

    if (incomingCallRef.current?.callId === callDetails.callId) {
      const mergedIncoming = { ...incomingCallRef.current, ...callDetails };
      incomingCallRef.current = mergedIncoming;
      setIncomingCall((current) => current?.callId === callDetails.callId ? mergedIncoming : current);
    }

    setWaitingCalls((prev) => {
      const hasCall = prev.some((call) => call.callId === callDetails.callId);
      if (!hasCall) return prev;

      const next = prev.map((call) => call.callId === callDetails.callId ? { ...call, ...callDetails } : call);
      waitingCallsRef.current = next;
      return next;
    });
  };

  const removeWaitingCall = (callId) => {
    if (!callId) return;
    setWaitingCalls((prev) => {
      const next = prev.filter((call) => call.callId !== callId);
      waitingCallsRef.current = next;
      return next;
    });
  };

  const enqueueWaitingCall = (callDetails) => {
    if (!callDetails?.callId) return;
    setWaitingCalls((prev) => {
      if (prev.some((call) => call.callId === callDetails.callId)) {
        return prev.map((call) => call.callId === callDetails.callId ? { ...call, ...callDetails } : call);
      }

      const next = [...prev, callDetails];
      waitingCallsRef.current = next;
      return next;
    });
  };

  const hydrateCallDetailsIfNeeded = (callDetails) => {
    const callId = callDetails?.callId;
    if (!callId || (callDetails.patientName && callDetails.driverName && callDetails.pharmacistId)) {
      return;
    }

    if (inFlightDetailsFetchRef.current.has(callId)) {
      return;
    }

    inFlightDetailsFetchRef.current.add(callId);
    fetchPortalDeliveryVideoCall(callId)
      .then((fetched) => {
        const normalized = normalizeCall(fetched);
        if (!normalized?.callId) return;
        updateQueuedCall(normalized);
      })
      .catch((error) => {
        console.error('Error refreshing portal delivery call details:', error);
      })
      .finally(() => {
        inFlightDetailsFetchRef.current.delete(callId);
      });
  };

  const promoteNextWaitingCall = (excludedCallId = null) => {
    if (activeCallRef.current?.callId || incomingCallRef.current?.callId || isSwitchingCallsRef.current) {
      return;
    }

    const availableCalls = waitingCallsRef.current.filter((call) => call.callId !== excludedCallId);
    const [nextCall, ...rest] = availableCalls;

    waitingCallsRef.current = rest;
    setWaitingCalls(rest);

    if (nextCall) {
      showPrimaryIncoming(nextCall);
      hydrateCallDetailsIfNeeded(nextCall);
      if (isAutoPickupEnabledForToday()) {
        window.setTimeout(() => {
          attemptAutoAccept(nextCall);
        }, 0);
      }
    }
  };

  const showCallNotice = (message) => {
    if (callNoticeTimeoutRef.current) {
      clearTimeout(callNoticeTimeoutRef.current);
    }

    setCallNotice(message);
    callNoticeTimeoutRef.current = setTimeout(() => {
      setCallNotice(null);
      callNoticeTimeoutRef.current = null;
    }, 5000);
  };

  const stopIncomingRingtone = () => {
    stopAllIncomingRingSounds();
    if (ringSoundRef.current) {
      ringSoundRef.current.stop();
      ringSoundRef.current = null;
    }
    setIsRingtoneBlocked(false);
  };

  const handleCrossTabCallAccepted = (callIds = []) => {
    const normalizedCallIds = callIds.map(getCallIdKey).filter(Boolean);
    if (normalizedCallIds.length === 0) return;

    normalizedCallIds.forEach((callId) => {
      acceptingRingtoneSuppressedCallIdsRef.current.add(callId);
    });

    setRingtoneSuppressedCallIds((previous) => {
      const next = new Set(previous);
      normalizedCallIds.forEach((callId) => {
        next.add(callId);
      });
      if (next.size === previous.size) return previous;
      return next;
    });

    if (incomingCallRef.current?.callId && normalizedCallIds.includes(getCallIdKey(incomingCallRef.current.callId))) {
      clearPrimaryIncoming();
    }

    setWaitingCalls((previous) => {
      const next = previous.filter((call) => !normalizedCallIds.includes(getCallIdKey(call.callId)));
      waitingCallsRef.current = next;
      return next;
    });

    stopIncomingRingtone();
  };

  const publishCrossTabCallAccepted = (callIds = []) => {
    if (typeof window === 'undefined') return;

    const normalizedCallIds = callIds.map(getCallIdKey).filter(Boolean);
    if (normalizedCallIds.length === 0) return;

    const message = {
      type: 'accepted',
      sourceTabId: tabIdRef.current,
      callIds: normalizedCallIds,
      pharmacyId,
      createdAt: Date.now(),
    };

    try {
      tabBroadcastChannelRef.current?.postMessage(message);
    } catch (error) {}

    try {
      window.localStorage?.setItem(DELIVERY_VIDEO_CALL_TAB_EVENT, JSON.stringify(message));
      window.localStorage?.removeItem(DELIVERY_VIDEO_CALL_TAB_EVENT);
    } catch (error) {}
  };

  const setAcceptingCallFlow = (nextValue) => {
    isAcceptingCallFlowRef.current = nextValue;
    setIsAcceptingCallFlow(nextValue);

    if (nextValue) {
      stopIncomingRingtone();
    }
  };

  const startIncomingRingtone = () => {
    stopIncomingRingtone();
    if (activeCallRef.current?.callId || isAcceptingCallFlowRef.current) {
      return;
    }

    const hasRingableIncomingCall =
      (incomingCallRef.current?.callId && !acceptingRingtoneSuppressedCallIdsRef.current.has(getCallIdKey(incomingCallRef.current.callId)))
      || waitingCallsRef.current.some((call) => !acceptingRingtoneSuppressedCallIdsRef.current.has(getCallIdKey(call.callId)));

    if (!hasRingableIncomingCall) {
      return;
    }

    ringSoundRef.current = createIncomingRingSound();
    ringSoundRef.current?.onBlocked?.(() => {
      setIsRingtoneBlocked(true);
    });
  };

  const stopRingtoneForAcceptedCall = (selectedCallId, { lockAcceptingFlow = false, notifyOtherTabs = false } = {}) => {
    if (lockAcceptingFlow) {
      setAcceptingCallFlow(true);
    }

    const callIdsToSuppress = [
      selectedCallId,
      incomingCallRef.current?.callId,
      ...waitingCallsRef.current.map((call) => call.callId),
    ].filter(Boolean);

    if (callIdsToSuppress.length > 0) {
      callIdsToSuppress.forEach((callId) => {
        acceptingRingtoneSuppressedCallIdsRef.current.add(getCallIdKey(callId));
      });

      setRingtoneSuppressedCallIds((previous) => {
        const next = new Set(previous);
        callIdsToSuppress.forEach((callId) => {
          next.add(getCallIdKey(callId));
        });

        if (next.size === previous.size) return previous;
        return next;
      });
    }

    if (notifyOtherTabs) {
      publishCrossTabCallAccepted(callIdsToSuppress);
    }

    stopIncomingRingtone();
  };

  const acceptIncomingCall = async (
    selectedCall,
    {
      failureMessage = 'Unable to accept the video call right now. Please try again.',
      optimisticHideIncoming = true,
    } = {}
  ) => {
    if (!selectedCall?.callId) return false;
    if (acceptingIncomingCallIdsRef.current.has(selectedCall.callId)) return false;

    const isPrimaryIncomingCall = incomingCallRef.current?.callId === selectedCall.callId;
    acceptingIncomingCallIdsRef.current.add(selectedCall.callId);
    const hasTabLock = await claimDeliveryVideoCallTabLock({
      pharmacyId,
      callId: selectedCall.callId,
      tabId: tabIdRef.current,
    });

    if (!hasTabLock) {
      acceptingIncomingCallIdsRef.current.delete(selectedCall.callId);
      return false;
    }

    let acceptedSuccessfully = false;
    stopRingtoneForAcceptedCall(selectedCall.callId, { lockAcceptingFlow: true });

    if (isPrimaryIncomingCall) {
      setLoadingAccept(true);
      if (optimisticHideIncoming) {
        setShowIncomingPopup(false);
        setIncomingCall(null);
        incomingCallRef.current = null;
      }
    }

    const optimisticCall = {
      ...selectedCall,
      status: 'accepting',
    };

    setActiveCall(optimisticCall);
    activeCallRef.current = optimisticCall;

    try {
      const accepted = normalizeCall(await acceptPortalDeliveryVideoCall(selectedCall.callId));
      const mergedAccepted = { ...selectedCall, ...accepted };
      acceptedSuccessfully = true;
      publishCrossTabCallAccepted([selectedCall.callId]);

      if (!optimisticHideIncoming && incomingCallRef.current?.callId === selectedCall.callId) {
        clearPrimaryIncoming();
      }

      setActiveCall((current) => (
        current?.callId === selectedCall.callId
          ? { ...current, ...mergedAccepted }
          : current
      ));
      activeCallRef.current = mergedAccepted;
      removeWaitingCall(selectedCall.callId);
      resetCallTracking(selectedCall.callId);
      return true;
    } catch (error) {
      console.error('Error accepting portal delivery call:', error);
      setActiveCall((current) => (current?.callId === selectedCall.callId ? null : current));
      activeCallRef.current = null;
      resetCallTracking(selectedCall.callId);

      if (!optimisticHideIncoming && isPrimaryIncomingCall) {
        const selectedCallIdKey = getCallIdKey(selectedCall.callId);
        acceptingRingtoneSuppressedCallIdsRef.current.delete(selectedCallIdKey);
        setRingtoneSuppressedCallIds((previous) => {
          if (!previous.has(selectedCallIdKey)) return previous;
          const next = new Set(previous);
          next.delete(selectedCallIdKey);
          return next;
        });
        incomingCallRef.current = selectedCall;
        setIncomingCall(selectedCall);
        setShowIncomingPopup(true);
      } else {
        promoteNextWaitingCall(selectedCall.callId);
      }

      showCallNotice(failureMessage);
      setAcceptingCallFlow(false);
      if (incomingCallRef.current?.callId || waitingCallsRef.current.length > 0) {
        startIncomingRingtone();
      }
      return false;
    } finally {
      if (!acceptedSuccessfully) {
        releaseDeliveryVideoCallTabLock({
          pharmacyId,
          callId: selectedCall.callId,
          tabId: tabIdRef.current,
        });
      }
      acceptingIncomingCallIdsRef.current.delete(selectedCall.callId);
      setAcceptingCallFlow(false);
      if (isPrimaryIncomingCall) {
        setLoadingAccept(false);
      }
    }
  };

  const attemptAutoAccept = async (callDetails) => {
    if (!callDetails?.callId || !isAutoPickupEnabledForToday()) {
      return false;
    }

    if (activeCallRef.current?.callId && activeCallRef.current.callId !== callDetails.callId) {
      return false;
    }

    return acceptIncomingCall(callDetails, {
      failureMessage: 'Auto pickup could not accept this video call. You can still accept it manually.',
      optimisticHideIncoming: false,
    });
  };

  const handleEnableAutoPickupAndAccept = async () => {
    if (!incomingCall?.callId || isAutoPickupUpdating) return;

    const selectedCall = incomingCall;
    stopRingtoneForAcceptedCall(selectedCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true });
    setIsAutoPickupUpdating(true);

    try {
      const response = await enablePortalDeliveryVideoCallAutoPickupSetting();
      applyAutoPickupSetting(response);
    } catch (error) {
      console.error('Error enabling delivery auto pickup setting:', error);
      showCallNotice('Auto pickup could not be enabled, but we will still try to connect this call.');
    } finally {
      setIsAutoPickupUpdating(false);
    }

    await acceptIncomingCall(selectedCall);
  };

  const handleDisableAutoPickup = async () => {
    if (!pharmacyId || !pharmacistId || isAutoPickupUpdating) return;

    setIsAutoPickupUpdating(true);
    try {
      const response = await disablePortalDeliveryVideoCallAutoPickupSetting();
      applyAutoPickupSetting(response);
    } catch (error) {
      console.error('Error disabling delivery auto pickup setting:', error);
      showCallNotice('Unable to turn off auto pickup calls right now. Please try again.');
    } finally {
      setIsAutoPickupUpdating(false);
    }
  };

  const handleEnableRingtone = async () => {
    const resumed = await ringSoundRef.current?.resume?.();
    setIsRingtoneBlocked(!resumed);
  };

  useEffect(() => () => {
    if (callNoticeTimeoutRef.current) {
      clearTimeout(callNoticeTimeoutRef.current);
      callNoticeTimeoutRef.current = null;
    }
    stopIncomingRingtone();
    processedMessageIdsRef.current.clear();
    processedRequestIdsRef.current.clear();
    acceptingIncomingCallIdsRef.current.clear();
    acceptingRingtoneSuppressedCallIdsRef.current.clear();
    isAcceptingCallFlowRef.current = false;
    setIsAcceptingCallFlow(false);
    setRingtoneSuppressedCallIds(new Set());
    inFlightDetailsFetchRef.current.clear();
    waitingCallsRef.current = [];
    activeCallRef.current = null;
    isSwitchingCallsRef.current = false;
  }, []);

  useEffect(() => {
    if (!shouldPlayIncomingRingtone) {
      stopIncomingRingtone();
      return undefined;
    }

    startIncomingRingtone();

    return () => {
      stopIncomingRingtone();
    };
  }, [shouldPlayIncomingRingtone]);

  // ---------------------------------------------------------------------------
  // Getting the pharmacist's attention when the portal is not the window they
  // are looking at.
  //
  // A page cannot raise its own window - every browser blocks that outright, or
  // any site could jump in front of whatever you were doing. The two things that
  // do work are a flashing tab title, which also flashes the Windows taskbar
  // button, and a desktop notification, which DOES focus this window and tab
  // when clicked. Both hang off shouldPlayIncomingRingtone so they inherit the
  // rules the ringtone already follows: nothing fires for a call that is being
  // accepted, already active, or suppressed by another tab.
  //
  // Neither carries the patient's name. The in-page popup shows it because the
  // pharmacist is sitting in front of it, but a tab title is visible in taskbar
  // previews and screen shares, and a desktop notification can surface on a lock
  // screen. The driver's name is enough to know the call is real.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!shouldPlayIncomingRingtone || typeof document === 'undefined') return undefined;

    // Captured after any previous run has restored it, so a re-run can never
    // record the flashing text as the title to go back to.
    const baseTitle = document.title;
    let showingAlert = false;

    const flash = () => {
      showingAlert = !showingAlert;
      document.title = showingAlert ? INCOMING_CALL_TITLE : baseTitle;
    };

    flash();
    const intervalId = setInterval(flash, TITLE_FLASH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      document.title = baseTitle;
    };
  }, [shouldPlayIncomingRingtone]);

  useEffect(() => {
    if (!shouldPlayIncomingRingtone) return undefined;
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return undefined;

    // Only when the portal is not the visible tab. If the pharmacist is already
    // looking at the popup, a desktop notification is pure noise - and noise is
    // how notifications end up switched off at the browser level, which would
    // cost us the one channel that reaches a minimised window.
    if (!document.hidden) return undefined;

    const ringingCall = incomingCallRef.current ?? waitingCallsRef.current[0] ?? null;
    let notification = null;
    let cancelled = false;

    const close = () => {
      try {
        notification?.close?.();
      } catch (error) {
        // A notification the user already dismissed throws on close; harmless.
      }
      notification = null;
    };

    const show = () => {
      if (cancelled || document.hidden === false) return;

      try {
        notification = new Notification('Incoming delivery call', {
          body: ringingCall?.driverName
            ? `${ringingCall.driverName} is requesting live pharmacist review.`
            : 'A driver is requesting live pharmacist review.',
          // Same tag per call means a re-ring replaces the card instead of
          // stacking a new one for every retry the driver's app makes.
          tag: ringingCall?.callId
            ? `delivery-video-call-${ringingCall.callId}`
            : 'delivery-video-call',
          requireInteraction: true,
          silent: true, // the in-page ringtone is already playing
        });

        notification.onclick = () => {
          // This is the part that actually brings the browser forward, and it
          // only works from inside a notification click.
          try {
            window.focus();
          } catch (error) {
            // Focus can be refused; the click still surfaces the window.
          }
          close();
        };
      } catch (error) {
        // Construction throws on some locked-down setups. The flashing title
        // and the ringtone still cover us, so this must never break the call.
      }
    };

    if (Notification.permission === 'granted') {
      show();
    } else if (Notification.permission === 'default') {
      // Asked here, on a real incoming call, rather than at page load. A prompt
      // with an obvious reason gets allowed; an unexplained one on login gets
      // blocked, and a block is painful to undo on a shared pharmacy machine.
      Notification.requestPermission()
        .then((permission) => {
          if (permission === 'granted') show();
        })
        .catch(() => {});
    }

    // Because requireInteraction keeps the card up until it is clicked, it has
    // to be pulled as soon as the pharmacist comes back to the tab - otherwise
    // it advertises a call they are already looking at.
    const handleVisibility = () => {
      if (!document.hidden) close();
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      close();
    };
  }, [shouldPlayIncomingRingtone]);

  // This subscription intentionally keys off pharmacy/user identity and uses refs
  // so queue helpers can stay synchronous without resubscribing on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pharmacyId || !pharmacistId) return undefined;

    const notificationChannel = 'pharmacy-notifications';
    const eventName = `pharmacy-${pharmacyId}`;
    const subscriptionId = `${notificationChannel}-${eventName}-delivery-video-call`;
// console.log('subscriptionId', subscriptionId);
// console.log('eventName', eventName);
    const handleIncomingRequest = async (payload) => {
      const callId = payload?.call_id;
      if (!callId) return;
      try {
        const detailsFromPayload = normalizeCall(payload);
        if (!detailsFromPayload?.callId) return;
        const details = detailsFromPayload;
        const requestKey = `${details.callId}:${details.status ?? 'requesting'}`;
        const now = Date.now();

        pruneOldEntries(processedRequestIdsRef, now);
        const lastProcessedAt = processedRequestIdsRef.current.get(requestKey);
        if (lastProcessedAt && now - lastProcessedAt < DUPLICATE_REQUEST_WINDOW_MS) {
          return;
        }
        processedRequestIdsRef.current.set(requestKey, now);

        if (details.pharmacistId != null && String(details.pharmacistId) !== String(pharmacistId)) return;
        if ((details.status ?? 'requesting') !== 'requesting') return;

        const autoPickupEnabled = isAutoPickupEnabledForToday();
        if (autoPickupEnabled) {
          notifyInactivityLogoutActivity();
        }

        if (activeCallRef.current?.callId === details.callId || incomingCallRef.current?.callId === details.callId) {
          updateQueuedCall(details);
          hydrateCallDetailsIfNeeded(details);
          return;
        }

        if (waitingCallsRef.current.some((call) => call.callId === details.callId)) {
          updateQueuedCall(details);
          hydrateCallDetailsIfNeeded(details);
          return;
        }

        if (activeCallRef.current?.callId && activeCallRef.current.callId !== details.callId) {
          enqueueWaitingCall(details);
          hydrateCallDetailsIfNeeded(details);
          return;
        }

        if (incomingCallRef.current?.callId && incomingCallRef.current.callId !== details.callId) {
          enqueueWaitingCall(details);
          hydrateCallDetailsIfNeeded(details);
          return;
        }

        showPrimaryIncoming(details);
        hydrateCallDetailsIfNeeded(details);
        if (autoPickupEnabled) {
          attemptAutoAccept(details);
        }
      } catch (error) {
        console.error('Error loading portal delivery call:', error);
      }
    };

    const onMessage = (message) => {
      const now = Date.now();
      pruneOldEntries(processedMessageIdsRef, now);

      if (message?.id) {
        const lastSeenAt = processedMessageIdsRef.current.get(message.id);
        if (lastSeenAt && now - lastSeenAt < DUPLICATE_REQUEST_WINDOW_MS) {
          return;
        }
        processedMessageIdsRef.current.set(message.id, now);
      }

      const payload = message?.data;
      if (!payload) return;
      if (payload.pharmacist_id != null && String(payload.pharmacist_id) !== String(pharmacistId)) return;

      if (payload.type === 'delivery_video_call_request') {
        handleIncomingRequest(payload);
        return;
      }

      if (payload.type === 'delivery_video_call_status') {
        const callId = payload.call_id;
        if (incomingCallRef.current?.callId === callId && ['accepted', ...TERMINAL_CALL_STATUSES].includes(payload.status)) {
          clearPrimaryIncoming();
          resetCallTracking(callId);
          if (!isSwitchingCallsRef.current) {
            promoteNextWaitingCall(callId);
          }
        }

        if (['accepted', ...TERMINAL_CALL_STATUSES].includes(payload.status)) {
          removeWaitingCall(callId);
          resetCallTracking(callId);
        }

        // Read from the ref, not state: this is the only place in the handler
        // that used the state value, and doing so forced activeCall?.callId into
        // the effect's dependency list, which tore down and rebuilt the Ably
        // subscription on every call start and end - see the note on that array.
        if (activeCallRef.current?.callId === callId && TERMINAL_CALL_STATUSES.includes(payload.status)) {
          setActiveCall(null);
          activeCallRef.current = null;
          if (!isSwitchingCallsRef.current) {
            promoteNextWaitingCall(callId);
          }
        }
      }
    };

    ablyConnectionManager.subscribe(notificationChannel, eventName, onMessage, subscriptionId);

    return () => {
      ablyConnectionManager.unsubscribe(subscriptionId);
      if (sessionSubscriptionIdRef.current) {
        ablyConnectionManager.unsubscribe(sessionSubscriptionIdRef.current);
        sessionSubscriptionIdRef.current = null;
      }
    };
    // Identity only. Adding activeCall?.callId here resubscribed on every call
    // start and end, and Ably drops anything published during that gap. The
    // "cancelled" event for a superseded call lands about a second after its
    // ring - squarely inside that window - so the waiting card it queued was
    // never removed and sat on screen advertising a call that no longer
    // existed. Clicking Accept on one then 409'd, because the server only
    // accepts a call still in `requesting`. A page refresh was the only cure.
  }, [pharmacyId, pharmacistId]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
  // Automatic pending-call polling is disabled. Incoming video calls are
  // handled by the real-time subscription above, so ordinary portal actions
  // never trigger the /pending request.
  // Backstop for a ring missed over realtime (tab asleep, Wi-Fi flip, Ably
  // reconnect losing the message): poll the backend for a currently-ringing call
  // on an interval and whenever the tab regains focus / comes back online, and
  // route it through the same queue helpers. Dedup below prevents double-ringing
  // a call the realtime path already surfaced.
  useEffect(() => {
    if (!pharmacyId || !pharmacistId) return undefined;

    let cancelled = false;

    const routePendingCall = (details) => {
      if (!details?.callId) return;
      if (details.pharmacistId != null && String(details.pharmacistId) !== String(pharmacistId)) return;
      if ((details.status ?? 'requesting') !== 'requesting') return;

      // Already being handled somewhere — leave it alone.
      if (activeCallRef.current?.callId === details.callId) return;
      if (incomingCallRef.current?.callId === details.callId) return;
      if (waitingCallsRef.current.some((call) => call.callId === details.callId)) return;

      // Busy with another call/incoming — queue it rather than interrupt.
      if (activeCallRef.current?.callId || incomingCallRef.current?.callId) {
        enqueueWaitingCall(details);
        hydrateCallDetailsIfNeeded(details);
        return;
      }

      showPrimaryIncoming(details);
      hydrateCallDetailsIfNeeded(details);
      if (isAutoPickupEnabledForToday()) {
        attemptAutoAccept(details);
      }
    };

    const pollPendingCall = async () => {
      try {
        const pending = await fetchPortalDeliveryVideoCallPending();
        if (cancelled || !pending) return;
        const details = normalizeCall(pending);
        if (details?.callId) {
          routePendingCall(details);
        }
      } catch (error) {
        // Network hiccups are expected; the realtime path and the next poll cover it.
      }
    };

    pollPendingCall();
    const intervalId = window.setInterval(pollPendingCall, 20000);

    const handleWake = () => {
      if (!document.hidden) pollPendingCall();
    };
    window.addEventListener('focus', handleWake);
    window.addEventListener('online', handleWake);
    document.addEventListener('visibilitychange', handleWake);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('online', handleWake);
      document.removeEventListener('visibilitychange', handleWake);
    };
  }, [pharmacyId, pharmacistId]); // eslint-disable-line react-hooks/exhaustive-deps
  */

  // Recovery only: if the tab was backgrounded or the browser reconnects, check
  // once for a call that may have arrived while realtime delivery was unavailable.
  // There is no interval and no action handler here, so normal portal activity
  // never creates `/pending` requests.
  useEffect(() => {
    if (!pharmacyId || !pharmacistId) return undefined;

    let cancelled = false;
    const minimumRecoveryIntervalMs = 60000;

    const recoverMissedPendingCall = async () => {
      const now = Date.now();
      if (now - lastPendingRecoveryCheckAtRef.current < minimumRecoveryIntervalMs) return;
      lastPendingRecoveryCheckAtRef.current = now;

      try {
        const pending = await fetchPortalDeliveryVideoCallPending();
        if (cancelled || !pending) return;

        const details = normalizeCall(pending);
        if (!details?.callId) return;
        if (details.pharmacistId != null && String(details.pharmacistId) !== String(pharmacistId)) return;
        if ((details.status ?? 'requesting') !== 'requesting') return;
        if (activeCallRef.current?.callId === details.callId) return;
        if (incomingCallRef.current?.callId === details.callId) return;
        if (waitingCallsRef.current.some((call) => call.callId === details.callId)) return;

        if (activeCallRef.current?.callId || incomingCallRef.current?.callId) {
          enqueueWaitingCall(details);
        } else {
          showPrimaryIncoming(details);
          if (isAutoPickupEnabledForToday()) attemptAutoAccept(details);
        }
        hydrateCallDetailsIfNeeded(details);
      } catch (error) {
        // The next reconnection or return to the portal will try again.
      }
    };

    const handleRecovery = () => {
      if (!document.hidden) recoverMissedPendingCall();
    };

    window.addEventListener('online', handleRecovery);
    document.addEventListener('visibilitychange', handleRecovery);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleRecovery);
      document.removeEventListener('visibilitychange', handleRecovery);
    };
  }, [pharmacyId, pharmacistId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeCall?.callId) return undefined;

    const channelName = 'video-call-channel';
    const eventName = `video-call-session-${activeCall.callId}`;
    const subscriptionId = `${channelName}-${eventName}-portal`;
    sessionSubscriptionIdRef.current = subscriptionId;

    ablyConnectionManager.subscribe(channelName, eventName, (message) => {
      if (TERMINAL_CALL_STATUSES.includes(message?.data?.status)) {
        setActiveCall(null);
        activeCallRef.current = null;
      }
    }, subscriptionId);

    return () => {
      ablyConnectionManager.unsubscribe(subscriptionId);
      if (sessionSubscriptionIdRef.current === subscriptionId) {
        sessionSubscriptionIdRef.current = null;
      }
    };
  }, [activeCall?.callId]);

  const handleAccept = async () => {
    if (!incomingCall?.callId || loadingAccept) return;
    stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true });
    await acceptIncomingCall(incomingCall);
  };

  const handleDecline = async () => {
    if (!incomingCall?.callId || loadingAccept) return;
    try {
      await declinePortalDeliveryVideoCall(incomingCall.callId);
    } catch (error) {
      console.error('Error declining portal delivery call:', error);
    } finally {
      resetCallTracking(incomingCall.callId);
      clearPrimaryIncoming();
      promoteNextWaitingCall(incomingCall.callId);
    }
  };

  const handleAcceptWaitingCall = async (callDetails) => {
    if (!callDetails?.callId || processingCallId) return;

    const hasActiveCall = activeCallRef.current?.callId && activeCallRef.current.callId !== callDetails.callId;
    if (hasActiveCall && confirmingWaitingCallId !== callDetails.callId) {
      setConfirmingWaitingCallId(callDetails.callId);
      return;
    }

    setProcessingCallId(callDetails.callId);
    setConfirmingWaitingCallId(null);
    stopRingtoneForAcceptedCall(callDetails.callId, { lockAcceptingFlow: true, notifyOtherTabs: true });
    isSwitchingCallsRef.current = true;
    let shouldPromoteAfterFailure = false;

    try {
      if (hasActiveCall) {
        await endPortalDeliveryVideoCall(activeCallRef.current.callId);
        setActiveCall(null);
        activeCallRef.current = null;
      }

      if (incomingCallRef.current?.callId && incomingCallRef.current.callId !== callDetails.callId) {
        const previousIncomingId = incomingCallRef.current.callId;
        clearPrimaryIncoming();
        await declinePortalDeliveryVideoCall(previousIncomingId, 'replaced');
        resetCallTracking(previousIncomingId);
      }

      const accepted = normalizeCall(await acceptPortalDeliveryVideoCall(callDetails.callId));
      removeWaitingCall(callDetails.callId);
      setActiveCall(accepted);
      activeCallRef.current = accepted;
      resetCallTracking(callDetails.callId);
    } catch (error) {
      console.error('Error accepting queued portal delivery call:', error);
      shouldPromoteAfterFailure = !activeCallRef.current?.callId && !incomingCallRef.current?.callId;
    } finally {
      isSwitchingCallsRef.current = false;
      setAcceptingCallFlow(false);
      setProcessingCallId(null);
      if (shouldPromoteAfterFailure) {
        promoteNextWaitingCall();
      }
    }
  };

  const handleDeclineWaitingCall = async (callDetails) => {
    if (!callDetails?.callId || processingCallId) return;

    setProcessingCallId(callDetails.callId);
    if (confirmingWaitingCallId === callDetails.callId) {
      setConfirmingWaitingCallId(null);
    }
    try {
      await declinePortalDeliveryVideoCall(callDetails.callId);
      removeWaitingCall(callDetails.callId);
      resetCallTracking(callDetails.callId);
    } catch (error) {
      console.error('Error declining queued portal delivery call:', error);
    } finally {
      setProcessingCallId(null);
    }
  };

  const handleCancelWaitingCallConfirmation = (callId) => {
    if (confirmingWaitingCallId === callId) {
      setConfirmingWaitingCallId(null);
    }
  };

  const handleEnd = async () => {
    if (!activeCall?.callId) {
      setActiveCall(null);
      activeCallRef.current = null;
      promoteNextWaitingCall();
      return;
    }
    try {
      await endPortalDeliveryVideoCall(activeCall.callId);
    } catch (error) {
      console.error('Error ending portal delivery call:', error);
    } finally {
      setActiveCall(null);
      activeCallRef.current = null;
      promoteNextWaitingCall(activeCall.callId);
    }
  };

  return (
    <>
      {callNotice && (
        <div className="fixed inset-x-0 top-4 z-[10001] flex justify-center px-4">
          <div className="w-full max-w-xl rounded-2xl border border-rose-300/40 bg-rose-50/95 px-4 py-3 text-sm font-medium text-rose-900 shadow-[0_16px_38px_rgba(15,23,42,0.18)] backdrop-blur-md">
            {callNotice}
          </div>
        </div>
      )}

      {showIncomingPopup && incomingCall && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-950/60 backdrop-blur-md p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-[32px] border border-white/20 bg-[linear-gradient(180deg,rgba(15,23,42,0.98)_0%,rgba(15,23,42,0.94)_100%)] text-white shadow-[0_28px_80px_rgba(15,23,42,0.45)]">
            <div className="border-b border-white/10 px-6 py-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-200/80">Incoming Video Call</p>
                  <h2 className="mt-3 text-[30px] leading-tight font-semibold text-white">
                    {getCallDisplayName(incomingCall)}
                  </h2>
                  {getCallReferenceName(incomingCall) && (
                    <p className="mt-1 text-xs font-medium text-slate-400">
                      Preferred Name: {getCallReferenceName(incomingCall)}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-slate-300">
                    {incomingCall.driverName || 'Assistant'} is requesting live pharmacist review.
                  </p>
                  {isRingtoneBlocked && (
                    <button
                      type="button"
                      onClick={handleEnableRingtone}
                      className="mt-3 rounded-xl border border-amber-300/45 bg-amber-300/12 px-3 py-2 text-xs font-semibold text-amber-50 transition-colors hover:bg-amber-300/18"
                    >
                      Enable ringtone
                    </button>
                  )}
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-400/30">
                  <span className="h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(74,222,128,0.85)]" />
                </div>
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="grid gap-3 rounded-[24px] border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Patient</span>
                  <span className="text-sm font-medium text-white">{getCallDisplayName(incomingCall)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Assistant</span>
                  <span className="text-sm font-medium text-white">{incomingCall.driverName || ''}</span>
                </div>
              </div>

              <div className={`mt-5 grid gap-2 ${autoPickupSetting.allowed ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={loadingAccept || isAutoPickupUpdating}
                  className="rounded-2xl border border-white/15 bg-white/5 px-3 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Decline
                </button>
                <button
                  type="button"
                  onPointerDown={() => stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                  onMouseDown={() => stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                  onTouchStart={() => stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                  onClick={handleAccept}
                  disabled={loadingAccept}
                  className="rounded-2xl border border-emerald-300/60 bg-[linear-gradient(135deg,#34d399_0%,#22c55e_55%,#16a34a_100%)] px-3 py-3 text-sm font-semibold text-slate-950 shadow-[0_0_0_4px_rgba(52,211,153,0.18),0_14px_30px_rgba(22,163,74,0.28)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_0_6px_rgba(52,211,153,0.24),0_18px_34px_rgba(22,163,74,0.32)] focus:outline-none focus:ring-4 focus:ring-emerald-300/40 animate-pulse disabled:cursor-not-allowed disabled:opacity-60 disabled:animate-none"
                >
                  {loadingAccept ? 'Connecting...' : 'Accept Call'}
                </button>
                {autoPickupSetting.allowed && (
                <button
                  type="button"
                  onClick={autoPickupEnabledForToday ? handleDisableAutoPickup : handleEnableAutoPickupAndAccept}
                  onPointerDown={() => {
                    if (!autoPickupEnabledForToday && incomingCall?.callId) {
                      stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true });
                    }
                  }}
                  onMouseDown={() => {
                    if (!autoPickupEnabledForToday && incomingCall?.callId) {
                      stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true });
                    }
                  }}
                  onTouchStart={() => {
                    if (!autoPickupEnabledForToday && incomingCall?.callId) {
                      stopRingtoneForAcceptedCall(incomingCall.callId, { lockAcceptingFlow: true, notifyOtherTabs: true });
                    }
                  }}
                  disabled={loadingAccept || isAutoPickupUpdating}
                  className="rounded-2xl border border-sky-300/30 bg-sky-500/10 px-3 py-3 text-sm font-semibold text-sky-100 transition-colors hover:bg-sky-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {autoPickupEnabledForToday
                    ? (isAutoPickupUpdating ? 'Turning Off...' : 'Turn Off Auto Pickup')
                    : (loadingAccept || isAutoPickupUpdating ? 'Auto Accepting...' : 'Auto Accept')}
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {waitingCalls.length > 0 && waitingNotificationsHost && createPortal(
        <div className="fixed right-4 top-4 z-[10030] flex w-[min(100%,24rem)] flex-col gap-3 pointer-events-none">
          {waitingCalls.map((call) => {
            const isProcessing = processingCallId === call.callId;
            const isConfirmingSwitch = confirmingWaitingCallId === call.callId;

            return (
              <div
                key={call.callId}
                className="pointer-events-auto overflow-hidden rounded-[24px] border border-white/20 bg-[linear-gradient(180deg,rgba(15,23,42,0.98)_0%,rgba(15,23,42,0.94)_100%)] p-4 text-white shadow-[0_18px_48px_rgba(15,23,42,0.35)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-200/80">Another Incoming Call</p>
                    <h3 className="mt-2 text-lg font-semibold leading-tight text-white">
                      {getCallDisplayName(call)}
                    </h3>
                    {getCallReferenceName(call) && (
                      <p className="mt-1 text-xs font-medium text-slate-400">
                        Preferred Name: {getCallReferenceName(call)}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-slate-300">
                      {call.driverName || 'Assistant'} is requesting live pharmacist review.
                    </p>
                    {isRingtoneBlocked && (
                      <button
                        type="button"
                        onClick={handleEnableRingtone}
                        className="mt-3 rounded-xl border border-amber-300/45 bg-amber-300/12 px-3 py-2 text-xs font-semibold text-amber-50 transition-colors hover:bg-amber-300/18"
                      >
                        Enable ringtone
                      </button>
                    )}
                  </div>
                  <span className="rounded-full border border-amber-300/30 bg-amber-300/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-100">
                    Waiting
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleDeclineWaitingCall(call)}
                    disabled={!!processingCallId}
                    className="rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm font-semibold text-slate-100 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isProcessing ? 'Working...' : 'Decline'}
                  </button>
                  <button
                    type="button"
                    onPointerDown={() => stopRingtoneForAcceptedCall(call.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                    onMouseDown={() => stopRingtoneForAcceptedCall(call.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                    onTouchStart={() => stopRingtoneForAcceptedCall(call.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                    onClick={() => handleAcceptWaitingCall(call)}
                    disabled={!!processingCallId}
                    className="rounded-2xl border border-emerald-300/60 bg-[linear-gradient(135deg,#34d399_0%,#22c55e_55%,#16a34a_100%)] px-3 py-2.5 text-sm font-semibold text-slate-950 shadow-[0_0_0_3px_rgba(52,211,153,0.14),0_12px_22px_rgba(22,163,74,0.22)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_0_5px_rgba(52,211,153,0.18),0_14px_24px_rgba(22,163,74,0.26)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isProcessing ? 'Working...' : isConfirmingSwitch ? 'Confirm Accept' : 'Accept'}
                  </button>
                </div>

                {isConfirmingSwitch && (
                  <div className="mt-2 rounded-2xl border border-white/12 bg-white/[0.04] px-3 py-2.5">
                    <p className="text-[13px] font-medium leading-5 text-slate-100">
                      Accepting this call will end your current video call.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onPointerDown={() => stopRingtoneForAcceptedCall(call.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                        onMouseDown={() => stopRingtoneForAcceptedCall(call.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                        onTouchStart={() => stopRingtoneForAcceptedCall(call.callId, { lockAcceptingFlow: true, notifyOtherTabs: true })}
                        onClick={() => handleAcceptWaitingCall(call)}
                        disabled={!!processingCallId}
                        className="flex-1 rounded-2xl border border-sky-300/35 bg-sky-500/12 px-3 py-2 text-sm font-semibold text-sky-100 transition-colors hover:bg-sky-500/18 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isProcessing ? 'Working...' : 'Yes, Switch Call'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCancelWaitingCallConfirmation(call.callId)}
                        disabled={!!processingCallId}
                        className="flex-1 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Keep Current Call
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>,
        waitingNotificationsHost
      )}

      {activeCall && (
        <PortalDeliveryCallWorkspace
          callData={activeCall}
          pharmacistName={pharmacistName}
          autoPickupEnabled={autoPickupEnabledForToday}
          isAutoPickupUpdating={isAutoPickupUpdating}
          onDisableAutoPickup={handleDisableAutoPickup}
          onClose={handleEnd}
        />
      )}
    </>
  );
};

export default PortalDeliveryVideoCallManager;
